import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Chip,
    IconButton,
    Breadcrumbs,
    Link,
    Select,
    MenuItem,
    FormControl,
    Divider,
    List,
    Collapse,
    CircularProgress,
    Alert,
    InputAdornment,
    Stack,
    Card,
    CardContent,
    CardActionArea,
    Tooltip,
    Badge,
    Switch,
    FormControlLabel
} from '@mui/material';
import {
    Search as SearchIcon,
    FilterList as FilterIcon,
    GridView as GridIcon,
    ViewList as ListIcon,
    Folder as FolderIcon,
    FolderOpen as FolderOpenIcon,
    InsertDriveFile as FileIcon,
    Description as DocumentIcon,
    PictureAsPdf as PdfIcon,
    Image as ImageIcon,
    VideoLibrary as VideoIcon,
    TableChart as SpreadsheetIcon,
    Code as CodeIcon,
    Archive as ZipIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    ExpandMore as ExpandMoreIcon,
    ChevronRight as ChevronRightIcon,
    Download as DownloadIcon,
    Share as ShareIcon,
    Info as InfoIcon,
    Close as CloseIcon,
    NavigateNext as NavigateNextIcon,
    Visibility as VisibilityIcon,
    Lock as LockIcon,
    Security as SecurityIcon,
    NoteAdd as NoteIcon,
    Computer as ComputerIcon
} from '@mui/icons-material';
import authService from '../services/auth.service';
import ImageViewer from '../components/ImageViewer';
import DocumentSharing from '../components/DocumentSharing';
import DocumentViewer from '../components/DocumentViewer';
import SensitiveDataSettings from '../components/SensitiveDataSettings';
import DocumentNotes from '../components/DocumentNotes';
import './DocumentsNew.css';

interface Document {
    _id: string;
    fileSyncId: {
        _id: string;
        fileName: string;
        fileSize: number;
        mimeType: string;
        machineId: string;
        syncedAt: string;
        path?: string;
    };
    status: string;
    category: string;
    tags: string[];
    extractedText?: string;
    ocrText?: string;
    ocrConfidence?: number;
    metadata?: any;
    thumbnailUrl?: string;
    processedAt?: string;
    public?: boolean;
    sharedWith?: string[];
    sharedDepartments?: string[];
    isSensitive?: boolean;
    sensitiveDataCategory?: string;
    notes?: any[];
}

interface FolderNode {
    id: string;
    name: string;
    path: string;
    children?: FolderNode[];
    fileCount: number;
}

const Documents: React.FC = () => {
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [selectedMachine, setSelectedMachine] = useState('all');
    const [machines, setMachines] = useState<any[]>([]);
    const [folders, setFolders] = useState<FolderNode[]>([]);
    const [selectedFolder, setSelectedFolder] = useState<string>('');
    const [documents, setDocuments] = useState<Document[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
    const [filterChips, setFilterChips] = useState<string[]>(['All Types']);
    const [savedSearches, setSavedSearches] = useState<string[]>([
        'Project Documentation',
        'Recent Invoices',
        'My Documents'
    ]);
    const [imageViewerOpen, setImageViewerOpen] = useState(false);
    const [sharingDialogOpen, setSharingDialogOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [documentContent, setDocumentContent] = useState<string>('');
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
    const [sensitiveSettingsOpen, setSensitiveSettingsOpen] = useState(false);
    const [sensitiveDocument, setSensitiveDocument] = useState<Document | null>(null);
    const [notesOpen, setNotesOpen] = useState(false);
    const [notesDocument, setNotesDocument] = useState<Document | null>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const fetchMachines = async () => {
        try {
            const api = authService.getApiInstance();
            const response = await api.get('/machines');
            setMachines(response.data.machines);
        } catch (error) {
            console.error('Failed to fetch machines:', error);
        }
    };

    const fetchFolderStructure = async (machineId: string) => {
        try {
            const api = authService.getApiInstance();
            
            if (machineId === 'all') {
                // Fetch folders from all machines
                const allFolders: any[] = [];
                const foldersByMachine: { [key: string]: any[] } = {};
                
                // Fetch folders for each machine
                for (const machine of machines) {
                    try {
                        const response = await api.get(`/sync/folders/${machine.machineId}`);
                        if (response.data.folders && response.data.folders.length > 0) {
                            foldersByMachine[machine.machineId] = response.data.folders.map((folder: any) => ({
                                ...folder,
                                machineId: machine.machineId,
                                machineName: machine.machineName || machine.machineId
                            }));
                        }
                    } catch (error) {
                        console.error(`Failed to fetch folders for machine ${machine.machineId}:`, error);
                    }
                }
                
                // Build tree structure with machines as root nodes
                setFolders(buildFolderTreeForAllMachines(foldersByMachine));
            } else {
                const response = await api.get(`/sync/folders/${machineId}`);
                setFolders(buildFolderTree(response.data.folders));
            }
        } catch (error) {
            console.error('Failed to fetch folder structure:', error);
        }
    };

    const buildFolderTree = (folders: any[]): FolderNode[] => {
        // Build hierarchical folder structure from flat list
        const folderMap = new Map<string, FolderNode>();
        const rootFolders: FolderNode[] = [];

        // Sort folders by path to ensure parents are processed before children
        const sortedFolders = [...folders].sort((a, b) => a.path.localeCompare(b.path));

        // First pass: create all folder nodes
        sortedFolders.forEach((folder) => {
            folderMap.set(folder.path, {
                id: folder._id || folder.path,
                name: folder.name,
                path: folder.path,
                fileCount: folder.fileCount,
                children: []
            });
        });

        // Second pass: build parent-child relationships
        sortedFolders.forEach((folder) => {
            const node = folderMap.get(folder.path)!;
            let parentFound = false;
            
            // Look for the deepest parent folder
            let currentPath = folder.path;
            // Handle both forward and backslashes
            while (currentPath.includes('/') || currentPath.includes('\\')) {
                // Get parent path by removing last segment
                const lastSlashIndex = Math.max(
                    currentPath.lastIndexOf('/'),
                    currentPath.lastIndexOf('\\')
                );
                if (lastSlashIndex <= 0) break;
                
                const parentPath = currentPath.substring(0, lastSlashIndex);
                
                // Check if this parent exists in our folder map
                if (folderMap.has(parentPath)) {
                    const parentNode = folderMap.get(parentPath)!;
                    // Only add if not already a child
                    if (!parentNode.children!.find(child => child.path === folder.path)) {
                        parentNode.children!.push(node);
                    }
                    parentFound = true;
                    break;
                }
                
                // Continue looking for a parent higher up
                currentPath = parentPath;
            }
            
            // Only add to root if no parent was found
            if (!parentFound) {
                rootFolders.push(node);
            }
        });

        // Third pass: calculate total file counts including subdirectories
        const calculateTotalFileCount = (node: FolderNode): number => {
            let total = node.fileCount;
            if (node.children && node.children.length > 0) {
                node.children.forEach(child => {
                    total += calculateTotalFileCount(child);
                });
            }
            // Store both counts
            (node as any).ownFileCount = node.fileCount;
            node.fileCount = total;
            return total;
        };

        rootFolders.forEach(root => calculateTotalFileCount(root));

        return rootFolders;
    };

    const buildFolderTreeForAllMachines = (foldersByMachine: { [key: string]: any[] }): FolderNode[] => {
        const machineNodes: FolderNode[] = [];
        
        Object.entries(foldersByMachine).forEach(([machineId, folders]) => {
            if (folders.length === 0) return;
            
            // Get machine name from the first folder
            const machineName = folders[0].machineName || machineId;
            
            // Build folder tree for this machine
            const machineFolders = buildFolderTree(folders);
            
            // Create machine node as root
            const machineNode: FolderNode = {
                id: `machine-${machineId}`,
                name: machineName,
                path: `machine:${machineId}`,
                fileCount: folders.reduce((sum, f) => sum + f.fileCount, 0),
                children: machineFolders
            };
            
            machineNodes.push(machineNode);
        });
        
        return machineNodes;
    };

    const fetchDocuments = useCallback(async () => {
        try {
            setLoading(true);
            const api = authService.getApiInstance();

            // Use search endpoint if there's a search query
            if (debouncedSearchQuery && debouncedSearchQuery.trim()) {
                const searchPayload: any = {
                    query: debouncedSearchQuery,
                    page: 1,
                    limit: 100,
                    filters: {}
                };

                // Add machine filter
                if (selectedMachine !== 'all') {
                    searchPayload.filters.machineId = selectedMachine;
                }

                // Add folder filter (but not for machine nodes)
                if (selectedFolder && !selectedFolder.startsWith('machine:')) {
                    searchPayload.filters.folder = selectedFolder;
                }

                // Add file type filters
                if (!filterChips.includes('All Types')) {
                    const typeFilters = filterChips.filter((chip) => ['PDF', 'Word', 'Images'].includes(chip));
                    if (typeFilters.length > 0) {
                        searchPayload.filters.fileTypes = typeFilters;
                    }
                }

                const response = await api.post('/documents/search', searchPayload);
                setDocuments(response.data.documents);
            } else {
                // Use regular documents endpoint without search
                const params = new URLSearchParams({
                    status: 'completed',
                    ...(selectedFolder && { folder: selectedFolder }),
                    ...(selectedMachine !== 'all' && { machineId: selectedMachine })
                });

                // Add filter params
                if (!filterChips.includes('All Types')) {
                    const typeFilters = filterChips.filter((chip) => ['PDF', 'Word', 'Images'].includes(chip));
                    if (typeFilters.length > 0) {
                        params.append('fileTypes', typeFilters.join(','));
                    }
                }

                const response = await api.get(`/documents?${params}`);
                setDocuments(response.data.documents);
            }
        } catch (error) {
            console.error('Failed to fetch documents:', error);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearchQuery, selectedFolder, filterChips, selectedMachine]);

    const handleFolderToggle = (folderId: string) => {
        setExpandedFolders((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(folderId)) {
                newSet.delete(folderId);
            } else {
                newSet.add(folderId);
            }
            return newSet;
        });
    };

    const handleFolderSelect = (folder: FolderNode) => {
        // Handle machine nodes differently
        if (folder.path.startsWith('machine:')) {
            // Select all files from this machine
            const machineId = folder.path.substring('machine:'.length);
            setSelectedMachine(machineId);
            setSelectedFolder('');
        } else {
            setSelectedFolder(folder.path);
        }
    };

    const handleDocumentSelect = (document: Document) => {
        setSelectedDocument(document);
        loadDocumentContent(document);
    };

    const loadDocumentContent = async (document: Document) => {
        try {
            setPreviewLoading(true);
            const api = authService.getApiInstance();
            const response = await api.get(`/documents/${document._id}/content`);
            setDocumentContent(response.data.text);
        } catch (error: any) {
            console.error('Failed to load document content:', error);
            if (error.response?.status === 403) {
                setDocumentContent(
                    'Access denied: This is a sensitive document and you do not have permission to view it.'
                );
            } else {
                setDocumentContent('Failed to load document content');
            }
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleFilterToggle = (filter: string) => {
        setFilterChips((prev) => {
            if (filter === 'All Types') {
                return ['All Types'];
            }
            const newFilters = prev.filter((f) => f !== 'All Types');
            if (prev.includes(filter)) {
                const updated = prev.filter((f) => f !== filter);
                return updated.length === 0 ? ['All Types'] : updated;
            } else {
                return [...newFilters.filter((f) => f !== filter), filter];
            }
        });
    };

    const handleSearch = () => {
        // fetchDocuments is automatically triggered by useEffect when searchQuery changes
        // This function is kept for explicit search actions (e.g., Enter key press)
        if (searchQuery.trim()) {
            fetchDocuments();
        }
    };

    const handleSaveSearch = () => {
        if (searchQuery) {
            setSavedSearches((prev) => [...prev, searchQuery]);
        }
    };

    const loadSavedSearch = (search: string) => {
        setSearchQuery(search);
        fetchDocuments();
    };

    const getFileIcon = (mimeType: string) => {
        if (mimeType?.includes('pdf')) return <PdfIcon />;
        if (mimeType?.includes('image')) return <ImageIcon />;
        if (mimeType?.includes('video')) return <VideoIcon />;
        if (mimeType?.includes('sheet') || mimeType?.includes('excel')) return <SpreadsheetIcon />;
        if (mimeType?.includes('word') || mimeType?.includes('document')) return <DocumentIcon />;
        if (mimeType?.includes('zip') || mimeType?.includes('rar')) return <ZipIcon />;
        if (mimeType?.includes('code') || mimeType?.includes('json') || mimeType?.includes('xml')) return <CodeIcon />;
        return <FileIcon />;
    };

    const getFileIconColor = (mimeType: string) => {
        if (mimeType?.includes('pdf')) return '#f44336';
        if (mimeType?.includes('image')) return '#4caf50';
        if (mimeType?.includes('video')) return '#ff5252';
        if (mimeType?.includes('sheet') || mimeType?.includes('excel')) return '#4caf50';
        if (mimeType?.includes('word') || mimeType?.includes('document')) return '#2196f3';
        if (mimeType?.includes('zip') || mimeType?.includes('rar')) return '#9c27b0';
        return '#757575';
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (date: string): string => {
        const d = new Date(date);
        const now = new Date();
        const diffMs = now.getTime() - d.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        return d.toLocaleDateString();
    };

    const handleQuickSensitiveToggle = async (doc: Document, isSensitive: boolean) => {
        try {
            const api = authService.getApiInstance();
            await api.patch(`/documents/${doc._id}/sensitive`, {
                isSensitive,
                // If marking as sensitive, set default values
                sensitiveDataCategory: isSensitive ? 'confidential' : undefined,
                allowedRoles: isSensitive ? ['admin', 'manager'] : undefined
            });

            // Update the document in the local state
            setDocuments(
                documents.map((d) =>
                    d._id === doc._id
                        ? { ...d, isSensitive, sensitiveDataCategory: isSensitive ? 'confidential' : undefined }
                        : d
                )
            );

            // Update selected document if it's the same
            if (selectedDocument?._id === doc._id) {
                setSelectedDocument({
                    ...selectedDocument,
                    isSensitive,
                    sensitiveDataCategory: isSensitive ? 'confidential' : undefined
                });
            }
        } catch (error) {
            console.error('Failed to update sensitive status:', error);
        }
    };

    const renderFolderTree = (nodes: FolderNode[], level: number = 0) => {
        return nodes.map((node, index) => {
            const isMachineNode = node.path.startsWith('machine:');
            const isSelected = selectedFolder === node.path || 
                              (isMachineNode && selectedMachine === node.path.substring('machine:'.length));
            
            return (
                <React.Fragment key={node.id}>
                    <div
                        className={`flex items-center px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                            isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                        }`}
                        style={{ paddingLeft: `${level * 16 + 16}px` }}
                        onClick={() => handleFolderSelect(node)}
                    >
                        <div className="flex items-center min-w-[36px]">
                            {node.children && node.children.length > 0 ? (
                                <>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleFolderToggle(node.id);
                                        }}
                                    >
                                        {expandedFolders.has(node.id) ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                                    </IconButton>
                                    {isMachineNode ? (
                                        <ComputerIcon color="primary" sx={{ mr: 1 }} />
                                    ) : expandedFolders.has(node.id) ? (
                                        <FolderOpenIcon color="primary" sx={{ mr: 1 }} />
                                    ) : (
                                        <FolderIcon color="primary" sx={{ mr: 1 }} />
                                    )}
                                </>
                            ) : isMachineNode ? (
                                <ComputerIcon color="primary" sx={{ ml: 1, mr: 1 }} />
                            ) : (
                                <FolderIcon color="primary" sx={{ ml: 1, mr: 1 }} />
                            )}
                        </div>
                        <div className="flex-1 ml-2">
                            <div className="text-sm font-medium">{node.name}</div>
                            <div className="text-xs text-gray-500">
                                {isMachineNode ? `${node.children?.length || 0} folders, ` : ''}
                                {node.fileCount} files
                                {(node as any).ownFileCount !== undefined && 
                                 (node as any).ownFileCount !== node.fileCount && 
                                 ` (${(node as any).ownFileCount} in this folder)`}
                            </div>
                        </div>
                        <div className="ml-auto">
                            <Chip label={node.fileCount} size="small" />
                        </div>
                    </div>
                    {node.children && expandedFolders.has(node.id) && (
                        <Collapse in={true}>{renderFolderTree(node.children, level + 1)}</Collapse>
                    )}
                </React.Fragment>
            );
        });
    };

    // useEffects
    useEffect(() => {
        fetchMachines();
    }, []);

    useEffect(() => {
        if (selectedMachine !== 'all') {
            fetchFolderStructure(selectedMachine);
        } else if (machines.length > 0) {
            // Fetch folders for all machines when 'all' is selected
            fetchFolderStructure('all');
        }
    }, [selectedMachine, machines]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    // Debounce search query
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500); // 500ms delay

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery]);

    return (
        <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: 'grey.50' }}>
            {/* Sidebar */}
            <Paper
                elevation={0}
                sx={{
                    width: 280,
                    borderRight: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Machine Selector */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <FormControl fullWidth size="small">
                        <Select
                            value={selectedMachine}
                            onChange={(e) => setSelectedMachine(e.target.value)}
                            displayEmpty
                        >
                            <MenuItem value="all">All Machines</MenuItem>
                            {machines.map((machine) => (
                                <MenuItem key={machine._id} value={machine.machineId}>
                                    {machine.machineName || machine.machineId}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Folder Tree */}
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    <List dense>
                        {folders.length > 0 ? (
                            renderFolderTree(folders)
                        ) : (
                            <div className="px-4 py-3">
                                <div className="text-sm font-medium">
                                    {selectedMachine === 'all' ? 'Loading folders...' : 'Select a machine'}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {selectedMachine === 'all' 
                                        ? 'Fetching folders from all machines' 
                                        : 'Choose a machine to browse folders'}
                                </div>
                            </div>
                        )}
                    </List>
                </Box>
            </Paper>

            {/* Main Content */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Search Section */}
                <Paper elevation={0} sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography variant="h5" gutterBottom>
                            Document Library
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Browse completed and processed documents ready for viewing
                        </Typography>
                    </Box>

                    {/* Search Bar */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search documents by name, content, OCR text, or metadata..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setSearchQuery('');
                                                setDebouncedSearchQuery('');
                                            }}
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        <Button variant="contained" onClick={handleSearch} sx={{ minWidth: 100 }}>
                            Search
                        </Button>
                        <Button variant="outlined" onClick={handleSaveSearch} sx={{ minWidth: 120 }}>
                            Save Search
                        </Button>
                    </Box>

                    {/* Filter Chips */}
                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                        {['PDF', 'Word', 'Images', 'All Types', 'This Week', 'This Month', 'OCR Text'].map((filter) => (
                            <Chip
                                key={filter}
                                label={filter}
                                color={filterChips.includes(filter) ? 'primary' : 'default'}
                                onClick={() => handleFilterToggle(filter)}
                                sx={{ mb: 1 }}
                            />
                        ))}
                    </Stack>

                    {/* Saved Searches */}
                    {savedSearches.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Saved Searches
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                {savedSearches.map((search) => (
                                    <Chip
                                        key={search}
                                        icon={<StarIcon />}
                                        label={search}
                                        variant="outlined"
                                        onClick={() => loadSavedSearch(search)}
                                        onDelete={() => setSavedSearches((prev) => prev.filter((s) => s !== search))}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    )}
                </Paper>

                {/* File Browser */}
                <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        {/* Toolbar */}
                        <Box
                            sx={{
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: 1,
                                borderColor: 'divider',
                                bgcolor: 'background.paper'
                            }}
                        >
                            {/* Breadcrumb */}
                            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
                                <Link underline="hover" color="inherit" href="#">
                                    All Documents
                                </Link>
                                {selectedFolder && (
                                    <Typography color="text.primary">{selectedFolder.split('/').pop()}</Typography>
                                )}
                            </Breadcrumbs>

                            {/* View Toggle */}
                            <Box>
                                <IconButton
                                    color={viewMode === 'grid' ? 'primary' : 'default'}
                                    onClick={() => setViewMode('grid')}
                                >
                                    <GridIcon />
                                </IconButton>
                                <IconButton
                                    color={viewMode === 'list' ? 'primary' : 'default'}
                                    onClick={() => setViewMode('list')}
                                >
                                    <ListIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Content Area */}
                        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                            {/* Search Results Summary */}
                            {debouncedSearchQuery && documents.length > 0 && (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    Found {documents.length} document{documents.length !== 1 ? 's' : ''} matching "
                                    {debouncedSearchQuery}" in content, OCR text, and metadata
                                </Alert>
                            )}

                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                    <CircularProgress />
                                </Box>
                            ) : documents.length === 0 ? (
                                <Alert severity="info">
                                    {debouncedSearchQuery
                                        ? `No documents found matching "${debouncedSearchQuery}". Full-text search includes document content and OCR text.`
                                        : 'No documents found. Try adjusting your search criteria.'}
                                </Alert>
                            ) : viewMode === 'grid' ? (
                                /* Grid View */
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                    {documents.map((doc, index) => (
                                        <Card
                                            sx={{
                                                cursor: 'pointer',
                                                border: selectedDocument?._id === doc._id ? 2 : 0,
                                                borderColor: 'primary.main',
                                                '&:hover': { boxShadow: 4 }
                                            }}
                                            onClick={() => handleDocumentSelect(doc)}
                                            key={index}
                                        >
                                            <CardContent sx={{ textAlign: 'center', position: 'relative' }}>
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 4,
                                                        right: 4,
                                                        display: 'flex',
                                                        gap: 0.5
                                                    }}
                                                >
                                                    <Tooltip
                                                        title={
                                                            doc?.notes?.length
                                                                ? `${doc.notes.length} note${doc.notes.length > 1 ? 's' : ''}`
                                                                : 'Note'
                                                        }
                                                    >
                                                        <IconButton
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setNotesDocument(doc);
                                                                setNotesOpen(true);
                                                            }}
                                                            size="small"
                                                        >
                                                            {doc?.notes?.length ? (
                                                                <Badge badgeContent={doc.notes.length} color="primary">
                                                                    <NoteIcon fontSize="small" color="action" />
                                                                </Badge>
                                                            ) : (
                                                                <NoteIcon fontSize="small" color="action" />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedDocument(doc);
                                                            setDocumentViewerOpen(true);
                                                        }}
                                                        size="small"
                                                        color="primary"
                                                    >
                                                        <VisibilityIcon />
                                                    </IconButton>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        fontSize: 48,
                                                        color: getFileIconColor(doc.fileSyncId.mimeType),
                                                        mb: 2
                                                    }}
                                                >
                                                    {getFileIcon(doc.fileSyncId.mimeType)}
                                                </Box>
                                                <Typography variant="body1" noWrap gutterBottom>
                                                    {doc.fileSyncId.fileName}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatFileSize(doc.fileSyncId.fileSize)} •{' '}
                                                    {formatDate(doc.fileSyncId.syncedAt)}
                                                </Typography>

                                                {/* Sensitive toggle and settings */}
                                                {(authService.getUser()?.role === 'admin' ||
                                                    authService.getUser()?.role === 'manager') && (
                                                    <Box
                                                        sx={{
                                                            mt: 2,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between'
                                                        }}
                                                    >
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    checked={doc.isSensitive || false}
                                                                    onChange={(e) => {
                                                                        e.stopPropagation();
                                                                        handleQuickSensitiveToggle(
                                                                            doc,
                                                                            e.target.checked
                                                                        );
                                                                    }}
                                                                    color="error"
                                                                    size="small"
                                                                />
                                                            }
                                                            label={
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Sensitive
                                                                </Typography>
                                                            }
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <Tooltip title="Sensitive settings">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSensitiveDocument(doc);
                                                                    setSensitiveSettingsOpen(true);
                                                                }}
                                                                color={doc.isSensitive ? 'error' : 'default'}
                                                            >
                                                                <SecurityIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                )}

                                                {/* Show sensitive indicator for non-admin users */}
                                                {doc.isSensitive &&
                                                    !(
                                                        authService.getUser()?.role === 'admin' ||
                                                        authService.getUser()?.role === 'manager'
                                                    ) && (
                                                        <Box sx={{ mt: 2 }}>
                                                            <Chip
                                                                icon={<LockIcon />}
                                                                label={doc.sensitiveDataCategory || 'Sensitive'}
                                                                size="small"
                                                                color="error"
                                                                variant="filled"
                                                            />
                                                        </Box>
                                                    )}
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            ) : (
                                /* List View */
                                <div className="space-y-2">
                                    {documents.map((doc) => (
                                        <div
                                            key={doc._id}
                                            className={`flex items-center px-4 py-3 rounded-lg cursor-pointer hover:bg-gray-50 ${
                                                selectedDocument?._id === doc._id
                                                    ? 'bg-blue-50 border border-blue-300'
                                                    : ''
                                            }`}
                                            onClick={() => handleDocumentSelect(doc)}
                                        >
                                            <div className="flex items-center min-w-[40px]">
                                                <Box sx={{ color: getFileIconColor(doc.fileSyncId.mimeType) }}>
                                                    {getFileIcon(doc.fileSyncId.mimeType)}
                                                </Box>
                                            </div>
                                            <div className="flex-1 ml-3">
                                                <div className="text-sm font-medium">{doc.fileSyncId.fileName}</div>
                                                <div className="text-xs text-gray-500">
                                                    {formatFileSize(doc.fileSyncId.fileSize)} •{' '}
                                                    {formatDate(doc.fileSyncId.syncedAt)}
                                                </div>
                                            </div>
                                            <div className="ml-auto flex items-center gap-2">
                                                {/* Sensitive indicator for non-admin users */}
                                                {doc.isSensitive &&
                                                    !(
                                                        authService.getUser()?.role === 'admin' ||
                                                        authService.getUser()?.role === 'manager'
                                                    ) && (
                                                        <Chip
                                                            icon={<LockIcon />}
                                                            label={doc.sensitiveDataCategory || 'Sensitive'}
                                                            size="small"
                                                            color="error"
                                                            variant="filled"
                                                        />
                                                    )}

                                                {/* Sensitive toggle for admin/manager */}
                                                {(authService.getUser()?.role === 'admin' ||
                                                    authService.getUser()?.role === 'manager') && (
                                                    <>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    checked={doc.isSensitive || false}
                                                                    onChange={(e) => {
                                                                        e.stopPropagation();
                                                                        handleQuickSensitiveToggle(
                                                                            doc,
                                                                            e.target.checked
                                                                        );
                                                                    }}
                                                                    color="error"
                                                                    size="small"
                                                                />
                                                            }
                                                            label=""
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <Tooltip title="Sensitive settings">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSensitiveDocument(doc);
                                                                    setSensitiveSettingsOpen(true);
                                                                }}
                                                                color={doc.isSensitive ? 'error' : 'default'}
                                                            >
                                                                <SecurityIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}

                                                <Tooltip
                                                    title={
                                                        doc?.notes?.length
                                                            ? `${doc.notes.length} note${doc.notes.length > 1 ? 's' : ''}`
                                                            : 'Note'
                                                    }
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedDocument(doc);
                                                            setDocumentViewerOpen(true);
                                                            // Open document viewer first, then notes
                                                            setTimeout(() => {
                                                                const noteButton =
                                                                    window.document.querySelector(
                                                                        '[aria-label="Notes"]'
                                                                    );
                                                                if (noteButton) {
                                                                    (noteButton as HTMLButtonElement).click();
                                                                }
                                                            }, 500);
                                                        }}
                                                    >
                                                        {doc?.notes?.length ? (
                                                            <Badge
                                                                badgeContent={doc.notes.length}
                                                                color="primary"
                                                                max={9}
                                                            >
                                                                <NoteIcon fontSize="small" />
                                                            </Badge>
                                                        ) : (
                                                            <NoteIcon fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>

                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedDocument(doc);
                                                        setDocumentViewerOpen(true);
                                                    }}
                                                    color="primary"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                                <Chip
                                                    label={doc.status === 'completed' ? 'Synced' : doc.status}
                                                    size="small"
                                                    color={doc.status === 'completed' ? 'success' : 'default'}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Box>
                    </Box>

                    {/* Preview Pane */}
                    {selectedDocument && !imageViewerOpen && (
                        <Paper
                            elevation={0}
                            sx={{
                                width: 400,
                                borderLeft: 1,
                                borderColor: 'divider',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            {/* Preview Header */}
                            <Box
                                sx={{
                                    p: 2,
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <Typography variant="h6" noWrap sx={{ maxWidth: 300 }}>
                                    {selectedDocument.fileSyncId.fileName}
                                </Typography>
                                <IconButton onClick={() => setSelectedDocument(null)}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>

                            {/* Preview Content */}
                            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                                {previewLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <>
                                        <Paper
                                            elevation={0}
                                            sx={{ p: 3, bgcolor: 'grey.50', minHeight: 400, position: 'relative' }}
                                        >
                                            {documentContent ? (
                                                <>
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            right: 8,
                                                            display: 'flex',
                                                            gap: 1
                                                        }}
                                                    >
                                                        <Tooltip
                                                            title={
                                                                selectedDocument?.notes?.length
                                                                    ? `${selectedDocument.notes.length} note${selectedDocument.notes.length > 1 ? 's' : ''}`
                                                                    : 'Note'
                                                            }
                                                        >
                                                            <IconButton
                                                                onClick={() => {
                                                                    setNotesDocument(selectedDocument);
                                                                    setNotesOpen(true);
                                                                }}
                                                                size="small"
                                                            >
                                                                {selectedDocument?.notes?.length ? (
                                                                    <Badge
                                                                        badgeContent={selectedDocument.notes.length}
                                                                        color="primary"
                                                                    >
                                                                        <NoteIcon fontSize="small" />
                                                                    </Badge>
                                                                ) : (
                                                                    <NoteIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <IconButton
                                                            onClick={() => setDocumentViewerOpen(true)}
                                                            color="primary"
                                                        >
                                                            <Tooltip title="View document">
                                                                <VisibilityIcon />
                                                            </Tooltip>
                                                        </IconButton>
                                                    </Box>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                        {documentContent.substring(0, 1000)}...
                                                    </Typography>
                                                </>
                                            ) : (
                                                <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                                                    <InfoIcon sx={{ fontSize: 48, mb: 2 }} />
                                                    <Typography>Select a document to preview</Typography>
                                                </Box>
                                            )}
                                        </Paper>

                                        {/* Metadata */}
                                        <Box sx={{ mt: 3 }}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Document Information
                                            </Typography>
                                            <div className="space-y-3">
                                                <div className="py-2">
                                                    <div className="text-sm font-medium text-gray-700">File Size</div>
                                                    <div className="text-sm text-gray-500">
                                                        {formatFileSize(selectedDocument.fileSyncId.fileSize)}
                                                    </div>
                                                </div>
                                                <div className="py-2">
                                                    <div className="text-sm font-medium text-gray-700">Type</div>
                                                    <div className="text-sm text-gray-500">
                                                        {selectedDocument.fileSyncId.mimeType}
                                                    </div>
                                                </div>
                                                <div className="py-2">
                                                    <div className="text-sm font-medium text-gray-700">Modified</div>
                                                    <div className="text-sm text-gray-500">
                                                        {formatDate(selectedDocument.fileSyncId.syncedAt)}
                                                    </div>
                                                </div>
                                                {selectedDocument.ocrConfidence && (
                                                    <div className="py-2">
                                                        <div className="text-sm font-medium text-gray-700">
                                                            OCR Confidence
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {selectedDocument.ocrConfidence}%
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </Box>
                                    </>
                                )}
                            </Box>

                            {/* Preview Actions */}
                            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant="contained"
                                        startIcon={<DownloadIcon />}
                                        onClick={async () => {
                                            try {
                                                const api = authService.getApiInstance();
                                                const response = await api.get(
                                                    `/documents/${selectedDocument._id}/download`,
                                                    {
                                                        responseType: 'blob'
                                                    }
                                                );

                                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                                const link = document.createElement('a');
                                                link.href = url;
                                                link.setAttribute('download', selectedDocument.fileSyncId.fileName);
                                                document.body.appendChild(link);
                                                link.click();
                                                link.remove();
                                                window.URL.revokeObjectURL(url);
                                            } catch (error) {
                                                console.error('Download failed:', error);
                                            }
                                        }}
                                    >
                                        Download
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<ShareIcon />}
                                        onClick={() => setSharingDialogOpen(true)}
                                    >
                                        Share
                                    </Button>
                                </Stack>
                            </Box>
                        </Paper>
                    )}
                </Box>
            </Box>

            {/* Document Viewer */}
            {selectedDocument && (
                <DocumentViewer
                    open={documentViewerOpen}
                    onClose={() => setDocumentViewerOpen(false)}
                    document={selectedDocument}
                />
            )}

            {/* Image Viewer */}
            {selectedDocument && (
                <ImageViewer
                    open={imageViewerOpen}
                    onClose={() => {
                        setImageViewerOpen(false);
                        setSelectedDocument(null);
                    }}
                    documentId={selectedDocument._id}
                    fileName={selectedDocument.fileSyncId.fileName}
                    fileSize={selectedDocument.fileSyncId.fileSize}
                    metadata={selectedDocument.metadata}
                />
            )}

            {/* Sharing Dialog */}
            {selectedDocument && (
                <DocumentSharing
                    open={sharingDialogOpen}
                    onClose={() => setSharingDialogOpen(false)}
                    documentId={selectedDocument._id}
                    documentName={selectedDocument.fileSyncId.fileName}
                    currentSharing={{
                        sharedWith: selectedDocument.sharedWith || [],
                        sharedDepartments: selectedDocument.sharedDepartments || [],
                        public: selectedDocument.public || false
                    }}
                    onUpdate={(sharingData) => {
                        setDocuments((prev) =>
                            prev.map((doc) => (doc._id === selectedDocument._id ? { ...doc, ...sharingData } : doc))
                        );
                        setSelectedDocument({ ...selectedDocument, ...sharingData });
                    }}
                />
            )}

            {/* Sensitive Data Settings */}
            {sensitiveDocument && (
                <SensitiveDataSettings
                    open={sensitiveSettingsOpen}
                    onClose={() => {
                        setSensitiveSettingsOpen(false);
                        setSensitiveDocument(null);
                    }}
                    document={sensitiveDocument}
                    onUpdate={() => {
                        fetchDocuments();
                    }}
                />
            )}

            {/* Document Notes Dialog */}
            {notesDocument && (
                <DocumentNotes
                    open={notesOpen}
                    onClose={() => {
                        setNotesOpen(false);
                        setNotesDocument(null);
                    }}
                    documentId={notesDocument._id}
                    onUpdate={() => {
                        // Refresh the documents list to update note counts
                        fetchDocuments();
                    }}
                />
            )}
        </Box>
    );
};

export default Documents;
